import { useCallback, useState } from 'react';

export type GeolocationCoords = {
  accuracy: number | null;
  latitude: number;
  longitude: number;
};

export type GeolocationErrorKind = 'denied' | 'unavailable' | 'timeout' | 'unsupported';

type UseGeolocationResult = {
  coords: GeolocationCoords | null;
  error: GeolocationErrorKind | null;
  errorMessage: string | null;
  isFetching: boolean;
  requestLocation: () => Promise<GeolocationCoords>;
};

const mapPositionError = (err: GeolocationPositionError): GeolocationErrorKind => {
  if (err.code === err.PERMISSION_DENIED) return 'denied';
  if (err.code === err.TIMEOUT) return 'timeout';
  return 'unavailable';
};

/**
 * Browser geolocation helper for one-shot position requests (e.g. CIA gate vend).
 * Prefer this over the IP-based customer geolocation store when GPS accuracy is required.
 */
const DEFAULT_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 0,
};

export function useGeolocation(): UseGeolocationResult {
  const [coords, setCoords] = useState<GeolocationCoords | null>(null);
  const [error, setError] = useState<GeolocationErrorKind | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  const requestLocation = useCallback(() => {
    if (!('geolocation' in navigator)) {
      const kind: GeolocationErrorKind = 'unsupported';
      const message = 'Geolocation is not supported by this browser.';
      setError(kind);
      setErrorMessage(message);
      return Promise.reject(new Error(message));
    }

    setIsFetching(true);
    setError(null);
    setErrorMessage(null);

    return new Promise<GeolocationCoords>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const next: GeolocationCoords = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          };
          setCoords(next);
          setIsFetching(false);
          resolve(next);
        },
        (err) => {
          const kind = mapPositionError(err);
          const message = err.message || 'Unable to get current location.';
          setError(kind);
          setErrorMessage(message);
          setIsFetching(false);
          reject(err);
        },
        DEFAULT_OPTIONS,
      );
    });
  }, []);

  return {
    coords,
    error,
    errorMessage,
    isFetching,
    requestLocation,
  };
}
